/**
 * Analytics Event Contract
 *
 * Universal property injection for all analytics events.
 * Ensures session linkage, environment, and platform context
 * are consistently included across all funnel events.
 *
 * NocoDB #901 - Taxonomy Instrumentation V2 (Session linkage consistency)
 *
 * Before (no session linkage):
 *   posthog.capture('analysis_started', { text_length: 450 })
 *   → { text_length: 450 }
 *
 * After (with universal properties):
 *   applyUniversalAnalyticsProperties({ eventName, properties, defaults })
 *   → { $session_id: 'abc-123', environment: 'production', platform: 'web',
 *       app_version: '0.15.0', text_length: 450 }
 */

export interface UniversalEventDefaults {
  /** PostHog session ID for session replay correlation (x-posthog-session-id header) */
  sessionId?: string;
  /** Deployment environment (production, staging, development) */
  environment?: string;
  /** Platform identifier (web, ios, android) */
  platform?: string;
  /** Application version (e.g. "0.15.0") */
  appVersion?: string;
}

/**
 * Applies universal analytics properties to an event's payload.
 *
 * Injects session linkage and environment context into every event,
 * ensuring PostHog session replay can correlate client and server events.
 *
 * The `$session_id` property is PostHog's standard field for linking
 * server-side events to a client session replay timeline.
 *
 * @param eventName - The analytics event name (unused; reserved for future per-event logic)
 * @param properties - Caller-provided event properties (override universal defaults)
 * @param defaults - Universal defaults to inject (session_id, env, platform, version)
 * @returns Merged properties with universal context applied
 */
export function applyUniversalAnalyticsProperties({
  properties,
  defaults,
}: {
  eventName?: string;
  properties?: Record<string, unknown>;
  defaults: UniversalEventDefaults;
}): Record<string, unknown> {
  const universal: Record<string, unknown> = {};

  // $session_id links this event to PostHog session replay timeline.
  // PostHog uses the $ prefix for built-in session correlation properties.
  if (defaults.sessionId) {
    universal['$session_id'] = defaults.sessionId;
  }

  if (defaults.environment) {
    universal['environment'] = defaults.environment;
  }

  if (defaults.platform) {
    universal['platform'] = defaults.platform;
  }

  if (defaults.appVersion) {
    universal['app_version'] = defaults.appVersion;
  }

  // Caller-provided properties take precedence over universal defaults.
  // This allows individual events to override platform/env when needed.
  return { ...universal, ...properties };
}
